import React, { useRef, useState, useEffect } from "react";
import { fetchAQI as fetchAQIService } from "../services/airQuality";
import { FaBinoculars } from "react-icons/fa";
import { useEmbeddedPage } from "../hooks/useEmbeddedPage";
import { supabase } from "../services/supabase";


function getWeatherIcon(forecast) {
  if (!forecast) return "☁️";
  const f = forecast.toLowerCase();
  if (f.includes("sun") || f.includes("clear")) return "☀️";
  if (f.includes("cloud")) return "☁️";
  if (f.includes("rain") || f.includes("shower")) return "🌧️";
  if (f.includes("snow")) return "❄️";
  if (f.includes("thunder")) return "⛈️";
  if (f.includes("fog")) return "🌫️";
  return "🌡️";
}

function WeatherBox({ loading, error, data }) {
  const periods = data && data.properties && Array.isArray(data.properties.periods)
    ? data.properties.periods.slice(0, 2)
    : [];
  return (
    <div className="bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">🌦️</span>
        <h3 className="font-semibold text-gray-900 dark:text-white">Weather Forecast - Weather.gov</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Loading weather...</span>
        </div>
      ) : error ? (
        <div className="text-sm text-red-500 dark:text-red-400">{error}</div>
      ) : periods.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {periods.map((period, idx) => (
            <div key={period.number || idx} className="bg-white dark:bg-gray-900/60 rounded-lg shadow p-3 flex flex-col gap-1 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800 dark:text-gray-100">{period.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{period.startTime ? new Date(period.startTime).toLocaleDateString() : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getWeatherIcon(period.shortForecast)}</span>
                <span className="text-gray-700 dark:text-gray-200">{period.shortForecast}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <span>🌡️ {period.temperature}°{period.temperatureUnit}</span>
                <span>💨 {period.windSpeed} {period.windDirection}</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{period.detailedForecast}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400">No weather data available.</div>
      )}
    </div>
  );
}

function AirQualityBox({ loading, error, data }) {
  return (
    <div className="bg-gradient-to-br from-green-100 to-green-50 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">🌫️</span>
        <h3 className="font-semibold text-gray-900 dark:text-white">Air Quality Index - AirNow.gov</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="animate-spin w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Loading air quality...</span>
        </div>
      ) : error ? (
        <div className="text-sm text-red-500 dark:text-red-400">{error}</div>
      ) : Array.isArray(data) && data.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.slice(0, 2).map((aqi, idx) => (
            <div key={aqi.DateObserved + aqi.ParameterName + idx} className="bg-white dark:bg-gray-900/60 rounded-lg shadow p-3 flex flex-col gap-1 border border-green-100 dark:border-green-800">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800 dark:text-gray-100">{aqi.ParameterName}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{aqi.DateObserved}</span>
              </div>
              <div className="flex items-center gap-2 text-lg">
                <span className="font-bold text-gray-900 dark:text-white">AQI: {aqi.AQI}</span>
                <span className="text-xs px-2 py-1 rounded bg-green-200 dark:bg-green-700 text-green-900 dark:text-green-100 font-semibold">{aqi.Category?.Name}</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Reporting Area: {aqi.ReportingArea}, {aqi.StateCode}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400">No air quality data available.</div>
      )}
    </div>
  );
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID;

const CATEGORIES = [
  { label: "Outdoor Rentals", query: "outdoor rentals" },
  { label: "Outdoor Activities", query: "outdoor activities" },
];

const MapPage = () => {
  const [aqiLoading, setAqiLoading] = useState(false);
  const [aqiError, setAqiError] = useState("");
  const [aqiData, setAqiData] = useState(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorNames, setVendorNames] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorCoords, setSelectedVendorCoords] = useState({ lat: null, lng: null });
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [weatherData, setWeatherData] = useState(null);
  const [showCategoryButtons, setShowCategoryButtons] = useState(false);

  useEffect(() => {
    const fetchVendorNames = async () => {
      const { data, error } = await supabase
        .from("vendor_chunks")
        .select("vendor_name")
        .order("vendor_name", { ascending: true });
      if (!error && Array.isArray(data)) {
        setVendorNames(data.map((row) => row.vendor_name).filter(Boolean));
      }
    };
    fetchVendorNames();
  }, []);

  // Handler for vendor click
  const handleVendorClick = async (vendorName) => {
    setSelectedVendor(vendorName);
    if (!map) return;
    // Fetch lat/lng for this vendor from Supabase
    const { data, error } = await supabase
      .from("vendor_chunks")
      .select("latitude,longitude")
      .eq("vendor_name", vendorName)
      .limit(1);
    if (!error && Array.isArray(data) && data.length === 1) {
      const { latitude, longitude } = data[0];
      if (typeof latitude === "number" && typeof longitude === "number") {
        setSelectedVendorCoords({ lat: latitude, lng: longitude });
        // Center map and add marker
        map.setCenter({ lat: latitude, lng: longitude });
        map.setZoom(15);
        // Remove previous markers
        markers.forEach((m) => m.setMap(null));
        setMarkers([]);
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: latitude, lng: longitude },
          title: vendorName,
        });
        setMarkers([marker]);
        // Fetch weather for this location
        fetchWeather(latitude, longitude);
        // Only fetch AQI if lat/lng are valid
        if (typeof latitude === "number" && typeof longitude === "number" && !isNaN(latitude) && !isNaN(longitude)) {
          fetchAQI(latitude, longitude);
        } else {
          setAqiData(null);
          setAqiError("");
        }
      } else {
        setSelectedVendorCoords({ lat: null, lng: null });
        setWeatherData(null);
        setWeatherError("");
        setAqiData(null);
        setAqiError("");
      }
    } else {
      setSelectedVendorCoords({ lat: null, lng: null });
      setWeatherData(null);
      setWeatherError("");
      setAqiData(null);
      setAqiError("");
    }
  };

  // Fetch AQI data for given lat/lng using shared utility
  async function fetchAQI(lat, lng) {
    setAqiLoading(true);
    setAqiError("");
    setAqiData(null);
    try {
      const json = await fetchAQIService(lat, lng);
      setAqiData(json);
    } catch (err) {
      setAqiError("Failed to fetch AQI data");
    } finally {
      setAqiLoading(false);
    }
  }

  // Fetch weather.gov data for given lat/lng
  async function fetchWeather(lat, lng) {
    setWeatherLoading(true);
    setWeatherError("");
    setWeatherData(null);
    try {
      const WEATHER_API_BASE = process.env.REACT_APP_WEATHER_API || "https://api.weather.gov";
      const pointsRes = await fetch(`${WEATHER_API_BASE}/points/${lat},${lng}`);
      if (!pointsRes.ok) {
        setWeatherError(`Failed to get weather grid: ${pointsRes.status}`);
        setWeatherLoading(false);
        return;
      }
      const pointsJson = await pointsRes.json();
      const forecastUrl = pointsJson?.properties?.forecast;
      if (!forecastUrl) {
        setWeatherError("No forecast URL found for this location.");
        setWeatherLoading(false);
        return;
      }
      const forecastRes = await fetch(forecastUrl);
      if (!forecastRes.ok) {
        setWeatherError(`Failed to get weather forecast: ${forecastRes.status}`);
        setWeatherLoading(false);
        return;
      }
      const forecastJson = await forecastRes.json();
      setWeatherData(forecastJson);
    } catch (err) {
      setWeatherError("Failed to fetch weather data");
    } finally {
      setWeatherLoading(false);
    }
  }

  // Google Maps loader and map setup (new API, with script injection)
  useEffect(() => {
    function injectGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps && window.google.maps.importLibrary) {
          resolve();
          return;
        }
        // Check if script already exists
        const existingScript = document.getElementById('google-maps-script');
        if (existingScript) {
          existingScript.addEventListener('load', resolve);
          existingScript.addEventListener('error', reject);
          return;
        }
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker&map_ids=${GOOGLE_MAP_ID || ''}`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }

    async function loadMap() {
      try {
        await injectGoogleMapsScript();
        // Now google.maps.importLibrary is available
        const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
          window.google.maps.importLibrary("maps"),
          window.google.maps.importLibrary("marker")
        ]);
        const centerLocation = { lat: 44.0582, lng: -121.3153 };
        const mapInstance = new Map(mapRef.current, {
          center: centerLocation,
          zoom: 13,
          mapId: GOOGLE_MAP_ID || "",
          zoomControl: true,
          gestureHandling: 'greedy',
        });
        setMap(mapInstance);
        new AdvancedMarkerElement({
          map: mapInstance,
          position: centerLocation,
          title: "Bend, OR (center)",
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load Google Maps:', err);
      }
    }
    loadMap();
    // eslint-disable-next-line
  }, []);

  // Search places by category
  const searchPlaces = async (query, centerOverride = null) => {
    if (!map) return;
    setLoading(true);
    markers.forEach((m) => m.setMap(null));
    setMarkers([]);

    const center = centerOverride || map.getCenter();

    const service = new google.maps.places.PlacesService(map);
    service.textSearch({
      query,
      location: center,
      radius: 5000
    }, async (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
        const { InfoWindow } = await google.maps.importLibrary("maps");
        // Reuse a single InfoWindow instance
        if (!window._infoWindow) {
          window._infoWindow = new InfoWindow();
        }
        const infoWindow = window._infoWindow;
        const newMarkers = results.map((place) => {
          const marker = new AdvancedMarkerElement({
            map,
            position: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
            title: place.name,
          });
          marker.addListener("click", () => {
            infoWindow.close();
            // Fetch place details using getDetails
            service.getDetails({ placeId: place.place_id }, (details, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && details) {
                let content = `<div style='font-family: Roboto, Arial, sans-serif; min-width:250px; max-width:320px;'>`;
                // Place name
                if (details.name) {
                  content += `<div style='font-weight: bold; font-size: 1.1em; margin-bottom: 4px;'>${details.name}</div>`;
                }
                // Address
                if (details.formatted_address) {
                  content += `<div style='margin-bottom: 4px;'>${details.formatted_address}</div>`;
                }
                // Phone
                if (details.formatted_phone_number) {
                  content += `<div style='margin-bottom: 4px;'><a href='tel:${details.formatted_phone_number}' style='color:#2563eb;text-decoration:none;'>${details.formatted_phone_number}</a></div>`;
                }
                // Website
                if (details.website) {
                  content += `<div style='margin-bottom: 4px;'><a href='${details.website}' target='_blank' rel='noopener noreferrer' style='color:#2563eb;'>Website</a></div>`;
                }
                // Opening hours (today only)
                if (details.opening_hours && Array.isArray(details.opening_hours.weekday_text)) {
                  const todayIdx = (new Date().getDay() + 6) % 7; // Google API: Monday=0, JS: Sunday=0
                  const todayHours = details.opening_hours.weekday_text[todayIdx];
                  if (todayHours) {
                    content += `<div style='margin-bottom: 4px;'>${todayHours}</div>`;
                  }
                }
                // Star rating
                if (details.rating) {
                  const stars = Math.round(details.rating);
                  content += `<div style='margin-bottom: 4px;'>Rating: ${details.rating} `;
                  for (let i = 0; i < 5; i++) {
                    content += i < stars ? "<span style='color:gold;'>&#9733;</span>" : "<span style='color:#ccc;'>&#9733;</span>";
                  }
                  content += `</div>`;
                }
                // Editorial summary
                if (details.editorial_summary && details.editorial_summary.overview) {
                  content += `<div style='margin-bottom: 4px; color:#444; font-style:italic;'>${details.editorial_summary.overview}</div>`;
                }
                // Types
                if (details.types && Array.isArray(details.types) && details.types.length > 0) {
                  content += `<div style='margin-bottom: 4px;'>Type: ${details.types.map(t => `<span style='background:#eee; border-radius:3px; padding:2px 6px; margin-right:3px;'>${t}</span>`).join('')}</div>`;
                }
                content += `</div>`;
                infoWindow.setContent(content);
                infoWindow.open({ map, anchor: marker });
              } else {
                infoWindow.setContent(`<div>Details not available.</div>`);
                infoWindow.open({ map, anchor: marker });
              }
            });
          });
          return marker;
        });
        setMarkers(newMarkers);
      }
      setLoading(false);
    });
  };

  // --- Main Render Block ---
  return (
    <div style={{ width: "100%" }}>
      <div style={{ height: "calc(100vh - 220px)", position: "relative", minHeight: 300, paddingLeft: 24, paddingRight: 24 }}>
        <div style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 2, display: "flex", gap: 12 }}>
          <button
            onClick={() => setShowCategoryButtons((v) => !v)}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 16px", borderRadius: 8, background: "#fff", color: "#222", border: "2px solid #2563eb", cursor: "pointer", fontWeight: "bold", fontSize: "1rem" }}
            aria-label="Binoculars"
          >
            <FaBinoculars style={{ width: 24, height: 24, color: "#2563eb" }} />
          </button>
          {showCategoryButtons && CATEGORIES.map((cat, idx) => (
            <button
              key={cat.label}
              onClick={() => {
                if (map) {
                  const center = map.getCenter();
                  searchPlaces(cat.query, center);
                }
              }}
              disabled={loading}
              style={{ padding: "8px 16px", borderRadius: 4, background: "#2563eb", color: "#fff", border: "none", cursor: "pointer", fontWeight: "bold", fontSize: "1rem" }}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div ref={mapRef} style={{ height: "100%", width: "100%", borderRadius: 24, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.08)" }} />
      </div>
      {/* Vendor list always visible below the map */}
      {/* Responsive row of two cards below map: vendor list and info card */}
      <div className="flex flex-col sm:flex-row justify-center items-stretch gap-6 mt-4 mb-8 w-full">
        <div className="bg-gradient-to-r from-green-400/80 to-blue-400/80 dark:from-green-700/80 dark:to-blue-700/80 border border-green-200 dark:border-green-700 rounded-xl p-4 sm:p-6 shadow-lg flex flex-col items-center justify-center w-full max-w-xl">
          <div style={{ height: 200, background: "transparent", overflowY: "auto", width: "100%" }}>
            <div style={{ padding: 16, fontWeight: "bold", borderBottom: "1px solid #eee", color: "#222" }}>All Vendors</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, color: "#222" }}>
              {vendorNames.map((name, idx) => (
                <li
                  key={idx}
                  style={{
                    padding: "8px 16px",
                    borderBottom: "1px solid #f4f4f4",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                    background: selectedVendor === name ? "#e0e7ff" : "transparent",
                    color: "#222"
                  }}
                  onClick={() => handleVendorClick(name)}
                >
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-400/80 to-blue-400/80 dark:from-green-700/80 dark:to-blue-700/80 border border-green-200 dark:border-green-700 rounded-xl p-4 sm:p-6 shadow-lg flex flex-col items-center justify-center w-full max-w-xl">
          <div className="w-full flex flex-col items-center justify-center h-full">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">Vendor Location</h3>
            {selectedVendor ? (
              selectedVendorCoords.lat !== null && selectedVendorCoords.lng !== null ? (
                <>
                  <div className="text-base text-gray-900 dark:text-white mb-1">Latitude: <span className="font-mono">{selectedVendorCoords.lat}</span></div>
                  <div className="text-base text-gray-900 dark:text-white">Longitude: <span className="font-mono">{selectedVendorCoords.lng}</span></div>
                  <div className="mt-4 w-full">
                    <WeatherBox loading={weatherLoading} error={weatherError} data={weatherData} />
                    <AirQualityBox loading={aqiLoading} error={aqiError} data={aqiData} />
                  </div>
                </>
              ) : (
                <div className="text-base text-gray-700 dark:text-gray-300">No coordinates found for this vendor.</div>
              )
            ) : (
              <div className="text-base text-gray-700 dark:text-gray-300">Select a vendor to view location.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
